import { useLocation } from "react-router";

import { urlHasEmployerParams } from "~/hec/tpa/lc-consent.logic";

import { getLearningCenterConsent } from "./guest-learning-data";

/**
 * TPA Learning Center consent gate (SFCC-11508).
 *
 * Port of hec-composable `overrides/app/hooks/use-learning-center-consent.ts`.
 * Predicates that drive the standalone consent modal:
 *
 *  - `hasRespondedtoConsentModal()` — the shopper has already answered (the
 *    localStorage consent flag is non-null, set by `saveLearningCenterConsent`
 *    on either choice). Used to render the modal AT MOST ONCE per browser.
 *  - `hasEmployerUrlParams()` — BOTH an employer id and an employee id are
 *    present (non-empty) on the current URL. This is the modal's trigger
 *    (COM-504): the business wants it to fire only for TPA marketing links that
 *    carry both ids. Accepted key spellings + matching rule live in
 *    `~/hec/tpa/lc-consent.logic`. The params are intentionally not persisted —
 *    once the shopper navigates away they're gone and the modal no longer
 *    qualifies.
 *
 * Both are returned as functions (not derived values) to match the composable
 * call shape and so the guard re-reads localStorage on each render rather than
 * caching a stale answer.
 */

interface UseLearningCenterConsentReturn {
  hasRespondedtoConsentModal: () => boolean;
  hasEmployerUrlParams: () => boolean;
}

export default function useLearningCenterConsent(): UseLearningCenterConsentReturn {
  const location = useLocation();

  const hasRespondedtoConsentModal = (): boolean =>
    getLearningCenterConsent() !== null;

  const hasEmployerUrlParams = (): boolean =>
    urlHasEmployerParams(location.search);

  return {
    hasRespondedtoConsentModal,
    hasEmployerUrlParams,
  };
}
